import { useCallback, useMemo, useState } from "react";
import { useFormContext } from "react-hook-form";
import { debounce } from "lodash";

import { useCheckPatientDuplicates } from "features/patients/hooks/useCheckPatientDuplicates";
import {
  shouldRunDuplicateCheck,
  toDuplicateCandidates,
} from "features/patients/utils/duplicateMatch";

// Fires the duplicate-patient check when a caller reports that firstName, surname or
// dateOfBirth was "left" (blurred, or a date picked). Debounced so tabbing through all
// three fields in quick succession sends one request with the final values instead of
// three. The check runs while the record is being typed and never on save: it informs
// the person entering it, it does not gate what they may create.
export const useDuplicatePatientCheck = ({ enabled }) => {
  const { getValues } = useFormContext();
  const [candidates, setCandidates] = useState([]);
  const [dismissed, setDismissed] = useState(false);
  const checkDuplicates = useCheckPatientDuplicates();

  const runCheck = useCallback(() => {
    if (!enabled) return;

    const { firstName, surname, dateOfBirth } = getValues();

    if (!shouldRunDuplicateCheck({ firstName, surname, dateOfBirth })) {
      setCandidates([]);
      return;
    }

    checkDuplicates.mutate(
      { firstName, surname, dateOfBirth },
      {
        onSuccess: (data) => {
          setDismissed(false);
          setCandidates(toDuplicateCandidates(data));
        },
        onError: () => {
          setCandidates([]);
        },
      }
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps -- checkDuplicates.mutate is stable per useMutation
  }, [enabled, getValues]);

  const onFieldBlur = useMemo(() => debounce(runCheck, 350), [runCheck]);

  return {
    onFieldBlur,
    candidates: dismissed ? [] : candidates,
    dismiss: () => setDismissed(true),
  };
};
